import {
  ArrowDropDown,
  AddCircleOutline,
  AddToPhotos,
} from '@mui/icons-material';
import {
  Backdrop,
  Box,
  Button,
  ButtonGroup,
  ClickAwayListener,
  Grow,
  Paper,
  Popper,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { clearNewOrder } from '../../../redux/actions/orders/newOrderActions';
import { clearServiceOrder } from '../../../redux/actions/orders/newServiceOrderActions';

export const options = [
  {
    label: 'createNew',
    url: '/orders/new',
    icon: <AddCircleOutline />,
  },
  {
    label: 'createNewServiceOrder',
    icon: <AddToPhotos />,
    url: '/orders/newServiceOrder',
  },
];

const useNavigation = buttonStatus => {
  const history = useHistory();
  const dispatch = useDispatch();

  return url => {
    if (!buttonStatus || !url) return;

    history.push(url);
    dispatch(clearNewOrder());
    dispatch(clearServiceOrder());
  };
};

export function SmallNewOrderAndServiceButtons({ buttonStatus }) {
  const { t } = useTranslation('orders');
  const [open, setOpen] = useState(false);
  const navigate = useNavigation(buttonStatus);

  const handleOpen = () => {
    if (!buttonStatus) return;
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  return (
    <>
      <Backdrop open={buttonStatus && open} sx={{ zIndex: 1251 }} />

      <Box
        sx={{
          position: 'fixed',
          bottom: 33,
          right: 16,
          zIndex: 1252,
        }}
      >
        <SpeedDial
          ariaLabel="SpeedDial"
          icon={<SpeedDialIcon />}
          onClose={handleClose}
          onOpen={handleOpen}
          open={buttonStatus ? open : false}
          FabProps={{ disabled: !buttonStatus }}
        >
          {options.map(action => (
            <SpeedDialAction
              key={action.label}
              icon={action.icon}
              tooltipTitle={t(action.label)}
              tooltipOpen={open}
              onClick={() => navigate(action.url)}
              FabProps={{ disabled: !buttonStatus }}
              sx={{
                '& .MuiSpeedDialAction-staticTooltipLabel': {
                  whiteSpace: 'nowrap',
                },
              }}
            />
          ))}
        </SpeedDial>
      </Box>
    </>
  );
}

export function LargeNewOrderAndServiceButtons({ buttonStatus }) {
  const { t } = useTranslation('orders');
  const [open, setOpen] = useState(false);
  const [selectedType, setSelectedType] = useState(0);
  const anchorRef = useRef(null);
  const navigate = useNavigation(buttonStatus);

  const handleToggle = () => {
    if (!buttonStatus) return;
    setOpen(prev => !prev);
  };

  const handleClose = event => {
    if (anchorRef.current?.contains(event.target)) return;
    setOpen(false);
  };

  const handleMenuItemClick = index => {
    if (!buttonStatus) return;

    setSelectedType(index);
    setOpen(false);
    navigate(options[index]?.url);
  };

  const handlePrimaryClick = () => {
    navigate(options[selectedType]?.url);
  };

  return (
    <>
      <ButtonGroup
        variant="contained"
        ref={anchorRef}
        sx={{ maxWidth: 225, minWidth: 225 }}
        disabled={!buttonStatus}
      >
        <Button fullWidth onClick={handlePrimaryClick} size="small">
          {t(options[selectedType]?.label)}
        </Button>
        <Button
          size="small"
          onClick={handleToggle}
          aria-expanded={open ? 'true' : undefined}
        >
          <ArrowDropDown fontSize="small" />
        </Button>
      </ButtonGroup>

      <Popper
        open={buttonStatus && open}
        anchorEl={anchorRef.current}
        transition
        disablePortal
        sx={{ zIndex: 1, pt: 1, width: 225 }}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <Stack p={0.5}>
                  {options.map((option, index) => (
                    <Button
                      key={index}
                      variant="text"
                      startIcon={option.icon}
                      fullWidth
                      disabled={!buttonStatus}
                      onClick={() => handleMenuItemClick(index)}
                      sx={{
                        justifyContent: 'flex-start',
                        color: theme => theme.palette.action.active,
                      }}
                    >
                      {t(option.label)}
                    </Button>
                  ))}
                </Stack>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
}

export default function NewOrderAndServiceOrderButtons({
  buttonStatus = true,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return isMobile ? (
    <SmallNewOrderAndServiceButtons buttonStatus={buttonStatus} />
  ) : (
    <LargeNewOrderAndServiceButtons buttonStatus={buttonStatus} />
  );
}
